import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { fmtDateTime } from "../lib/api";
import { Field, StatusPill } from "../components/Primitives";
import FileUploader from "../components/FileUploader";
import { toast } from "sonner";
import { ArrowLeft, ClipboardCheck, FilePlus } from "lucide-react";

const INSPECTION_TEMPLATE = [
  { section: "Exterior", items: ["Body panels", "Paint condition", "Bumpers", "Lights", "Glass"] },
  { section: "Interior", items: ["Seats", "Dashboard", "Infotainment", "AC / Climate", "Electricals"] },
  { section: "Engine Bay", items: ["Oil level", "Coolant", "Belts & hoses", "Battery", "Leaks"] },
  { section: "Undercarriage", items: ["Brakes", "Suspension", "Exhaust", "Tyres", "Steering"] },
  { section: "Road Test", items: ["Acceleration", "Braking", "Noise / Vibration", "Alignment"] },
];

export default function IntakeDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [intake, setIntake] = useState(null);
  const [inspection, setInspection] = useState([]);
  const [notes, setNotes] = useState("");

  const load = async () => {
    const { data } = await api.get(`/intakes/${id}`);
    setIntake(data);
    if (data.inspection?.items) {
      setInspection(data.inspection.items);
      setNotes(data.inspection.overall_notes || "");
    } else {
      const flat = [];
      INSPECTION_TEMPLATE.forEach((s) => s.items.forEach((label) => flat.push({ section: s.section, label, status: "pass", notes: "" })));
      setInspection(flat);
    }
  };
  useEffect(() => { load(); }, [id]);

  if (!intake) return <div>Loading…</div>;

  const setItem = (i, patch) => {
    const next = [...inspection];
    next[i] = { ...next[i], ...patch };
    setInspection(next);
  };

  const saveInspection = async () => {
    try {
      await api.post("/inspections", { intake_id: id, items: inspection, overall_notes: notes });
      toast.success("Inspection saved");
      load();
    } catch { toast.error("Failed"); }
  };

  const createJobCard = async () => {
    try {
      const { data } = await api.post("/job-cards", {
        intake_id: id, lines: [], advance_required: 0,
      });
      toast.success("Job card opened");
      nav(`/service/jobcards/${data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const saveImages = async (ids) => {
    await api.put(`/intakes/${id}`, { ...intake, image_ids: ids });
    setIntake({ ...intake, image_ids: ids });
  };

  return (
    <div>
      <button onClick={() => nav(-1)} className="kk-btn-ghost mb-4" data-testid="back"><ArrowLeft size={14}/> Back</button>
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className="flex items-start justify-between pb-4 border-b border-[var(--kk-border)]">
            <div>
              <div className="kk-overline">Intake</div>
              <div className="font-display text-3xl uppercase">{intake.code}</div>
              <div className="text-sm mt-1">{intake.make} {intake.model} · {intake.registration_no}</div>
              <div className="text-xs text-[var(--kk-muted)]">{intake.customer_name} · {intake.phone}</div>
            </div>
            <div className="text-right">
              <StatusPill status={intake.status} />
              <div className="text-xs text-[var(--kk-muted)] mt-1 font-mono">{fmtDateTime(intake.created_at)}</div>
              <div className="text-xs font-mono">KMs {intake.kms_in?.toLocaleString?.()}</div>
            </div>
          </div>

          <div className="mt-6">
            <div className="font-display text-lg uppercase mb-3">Walk-around Photos</div>
            <FileUploader
              category="intake"
              ids={intake.image_ids || []}
              onChange={saveImages}
              label="Damage / condition photos"
            />
          </div>

          <div className="mt-8">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="kk-overline">Multi-point</div>
                <div className="font-display text-lg uppercase">Digital Inspection</div>
              </div>
              <button className="kk-btn-primary" onClick={saveInspection} data-testid="save-inspection">
                <ClipboardCheck size={14} /> Save Inspection
              </button>
            </div>
            {INSPECTION_TEMPLATE.map((sec) => (
              <div key={sec.section} className="mb-5">
                <div className="kk-overline mb-2">{sec.section}</div>
                <div className="space-y-2">
                  {inspection.filter((it) => it.section === sec.section).map((it, _i) => {
                    const idx = inspection.indexOf(it);
                    return (
                      <div key={idx} className="grid grid-cols-12 items-center gap-3 border-b border-[var(--kk-border)] pb-2">
                        <div className="col-span-5 text-sm">{it.label}</div>
                        <div className="col-span-4 flex gap-1">
                          {["pass", "attention", "fail"].map((s) => (
                            <button
                              key={s}
                              onClick={() => setItem(idx, { status: s })}
                              className={"kk-pill text-[0.6rem] " + (it.status === s ? (s === "pass" ? "kk-pill-lime" : s === "fail" ? "bg-[var(--kk-danger)] text-white" : "bg-[var(--kk-warning)] text-black") : "kk-pill-muted")}
                              data-testid={`insp-${idx}-${s}`}
                            >
                              {s}
                            </button>
                          ))}
                        </div>
                        <input
                          placeholder="notes"
                          value={it.notes || ""}
                          onChange={(e) => setItem(idx, { notes: e.target.value })}
                          className="kk-input col-span-3 py-1 text-xs"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            <Field label="Overall notes">
              <textarea className="kk-input min-h-[80px]" value={notes} onChange={(e) => setNotes(e.target.value)} />
            </Field>
          </div>
        </div>

        <div>
          <div className="kk-card p-5 sticky top-20">
            <div className="kk-overline">Reported Issues</div>
            <div className="text-sm mt-1">{intake.reported_issues || "—"}</div>
            <div className="kk-overline mt-4">Damage Notes</div>
            <div className="text-sm mt-1">{intake.damage_notes || "—"}</div>
            <div className="mt-6 border-t border-[var(--kk-border)] pt-4">
              {intake.job_card_id ? (
                <button onClick={() => nav(`/service/jobcards/${intake.job_card_id}`)} className="kk-btn-primary w-full justify-center">
                  Open Job Card
                </button>
              ) : (
                <button onClick={createJobCard} className="kk-btn-primary w-full justify-center" data-testid="open-jobcard">
                  <FilePlus size={14} /> Open Job Card
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
